import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Drawer from "../../../Component/Drawer";
import Pill from "../../../Component/Pill";
import { DemoTenant, RESELLER_PLANS } from "../../../utils/staticData";

interface TenantDrawerProps {
  show: boolean;
  tenant: DemoTenant | null; // null = new reseller
  onClose: () => void;
  onSave: (t: DemoTenant) => void;
}

const EMPTY: DemoTenant = { name: "", domain: "", owner: "", clients: 0, mrr: 0, color: "#117465", sec: "#0f5132", ssl: "warn", dns: "bad", margin: 25, status: "pending" };

// Branding + domain setup for a reseller, with a live preview of their app
const TenantDrawer = ({ show, tenant, onClose, onSave }: TenantDrawerProps) => {
  const [form, setForm] = useState<DemoTenant>(EMPTY);
  const [email, setEmail] = useState("");
  const [support, setSupport] = useState("");
  const [allowed, setAllowed] = useState<string[]>([]);

  useEffect(() => {
    if (!show) return;
    const t = tenant || EMPTY;
    setForm(t);
    setEmail(t.owner ? `${t.owner.split(" ")[0].toLowerCase()}@${t.domain.split(".").slice(-2).join(".")}` : "");
    setSupport("");
    setAllowed(RESELLER_PLANS.filter(p => p !== "Lifetime"));
  }, [show, tenant]);

  const set = <K extends keyof DemoTenant>(k: K, v: DemoTenant[K]) => setForm(f => ({ ...f, [k]: v }));
  const togglePlan = (p: string) => setAllowed(a => (a.includes(p) ? a.filter(x => x !== p) : [...a, p]));

  const save = () => {
    if (!form.name.trim() || !form.domain.trim()) return toast.warning("Brand name and custom domain are required");
    onSave(form);
  };

  return (
    <Drawer
      show={show}
      onClose={onClose}
      title={tenant ? `${tenant.name} · branding` : "New reseller"}
      subtitle={<>Maps to <span className="mono">/common/tenantConfig/{"{host}"}</span></>}
      footer={<>
        <button type="button" className="cx-btn cx-btn-light" onClick={onClose}>Cancel</button>
        <button type="button" className="cx-btn cx-btn-primary" onClick={save}>Save &amp; publish</button>
      </>}
    >
      <div className="cx-bp">
        <div className="top" style={{ background: form.color }}>
          <span style={{ width: 22, height: 22, borderRadius: 6, background: "#ffffff40" }}></span>
          <span>{form.name || "Brand name"}</span>
        </div>
        <div className="body">
          <div className="side" style={{ background: form.sec }}><span></span><span></span><span></span><span></span></div>
          <div className="main">
            <div className="blk"></div>
            <div className="blk" style={{ height: 70 }}></div>
            <div className="btnp" style={{ background: form.color }}>New broadcast</div>
          </div>
        </div>
      </div>

      <div className="cx-fgrid" style={{ marginTop: 16 }}>
        <div>
          <label className="cx-label">Brand name</label>
          <input className="cx-input" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="cx-label">Custom domain</label>
          <input className="cx-input mono" placeholder="app.brand.com" value={form.domain} onChange={(e) => set("domain", e.target.value)} />
        </div>
        <div>
          <label className="cx-label">Primary color</label>
          <div style={{ display: "flex", gap: 8 }}>
            <div className="cx-swatch" style={{ background: form.color }}>
              <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} aria-label="Primary color" />
            </div>
            <input className="cx-input mono" value={form.color} onChange={(e) => set("color", e.target.value)} />
          </div>
        </div>
        <div>
          <label className="cx-label">Secondary color</label>
          <div style={{ display: "flex", gap: 8 }}>
            <div className="cx-swatch" style={{ background: form.sec }}>
              <input type="color" value={form.sec} onChange={(e) => set("sec", e.target.value)} aria-label="Secondary color" />
            </div>
            <input className="cx-input mono" value={form.sec} onChange={(e) => set("sec", e.target.value)} />
          </div>
        </div>
        <div>
          <label className="cx-label">Logo</label>
          <div className="cx-upload"><span><i className="ri-upload-cloud-2-line"></i> Upload PNG/SVG</span></div>
        </div>
        <div>
          <label className="cx-label">Favicon</label>
          <div className="cx-upload"><span><i className="ri-upload-cloud-2-line"></i> 32×32 ICO/PNG</span></div>
        </div>
        <div>
          <label className="cx-label">Owner / login email</label>
          <input className="cx-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="cx-label">Wholesale margin %</label>
          <input className="cx-input mono" inputMode="numeric" value={form.margin} onChange={(e) => set("margin", Number(e.target.value.replace(/\D/g, "")) || 0)} />
        </div>
        <div className="full">
          <label className="cx-label">Support email &amp; WhatsApp shown to their clients</label>
          <input className="cx-input" placeholder="support@brand.com · +91…" value={support} onChange={(e) => setSupport(e.target.value)} />
        </div>
      </div>

      <h6 className="cx-h6">Domain setup</h6>
      <div className="cx-orow">
        <div className={`cx-ic ${form.dns === "ok" ? "ok" : "bad"}`}><i className="ri-global-line"></i></div>
        <div>
          <div className="t">CNAME record</div>
          <div className="d mono">{form.domain || "app.brand.com"} → tenants.uomox.com</div>
        </div>
        <div className="right">
          <Pill tone={form.dns === "ok" ? "ok" : "bad"}>{form.dns === "ok" ? "verified" : "not found"}</Pill>
          <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => toast.info("DNS re-checked")}>Re-check</button>
        </div>
      </div>
      <div className="cx-orow">
        <div className={`cx-ic ${form.ssl === "ok" ? "ok" : "warn"}`}><i className="ri-lock-line"></i></div>
        <div>
          <div className="t">SSL certificate</div>
          <div className="d">Auto-issued after DNS verify (Let's Encrypt)</div>
        </div>
        <div className="right"><Pill tone={form.ssl === "ok" ? "ok" : "warn"}>{form.ssl === "ok" ? "active" : "pending"}</Pill></div>
      </div>

      <h6 className="cx-h6">Allowed plans for this reseller</h6>
      <div className="cx-chips">
        {RESELLER_PLANS.map(p => (
          <button type="button" key={p} className={`cx-chip ${allowed.includes(p) ? "on" : ""}`} onClick={() => togglePlan(p)}>{p}</button>
        ))}
      </div>
    </Drawer>
  );
};

export default TenantDrawer;
